/* UTILS */
import router from "@/router"
import { setup, createActor, fromPromise } from "xstate"
import { globalActor } from "@/machines/globalMachine"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { type GlobalMachineEvent } from "@/machines/types"

/* HELPERS */
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useGotoParam } from "@/composables/useGoto"
import { usePendingUser } from "@/composables/usePendingUser"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

/* STORES */
import { useUserStore } from "@/stores/user"
import { useAnswersStore } from "@/features/screening/store/answers.store"
import { storeToRefs } from "pinia"
import type { NextAssessmentData } from "@/types/next-assessment"

const { removeGotoParam, isGotoParam } = useGotoParam()

type LoadScreeningDataResult =
  | { type: "consent_needed"; data?: NextAssessmentData }
  | { type: "redirected"; data?: NextAssessmentData }
  | { type: "assessment"; data?: NextAssessmentData }

const deleteSessionItem = () => {
  useSessionStorage().deleteSessionItem("origin")
  removeGotoParam()
}

// Loader for Screening datas
const loadScreeningData = fromPromise((): Promise<LoadScreeningDataResult> =>
  withAssessmentLoadingMinDuration("SCAN", async () => {
    const userStore = useUserStore()
    const { fetchScreeningData } = useAnswersStore()
    const { isConsentNeeded } = usePendingUser()

    const { nextAssessment } = storeToRefs(userStore)

    if (isConsentNeeded.value) {
      return { type: "consent_needed" }
    }

    if (!nextAssessment.value) {
      await fetchScreeningData()
    }

    const assessmentData = nextAssessment.value?.next_assessment

    if (!nextAssessment.value || !assessmentData?.assessment) {
      deleteSessionItem()
      throw new Error("NO_NEXT_ASSESSMENT")
    }

    if (assessmentData?.assessment !== "screening_interview") {
      deleteSessionItem()

      return { type: "redirected", data: assessmentData }
    }

    return { type: "assessment", data: assessmentData }
  })
)

export const screeningMachine = setup({
  actors: {
    loadScreeningData,
  },
  types: {} as {
    events: any
    context: {
      errorSource?: string
    }
  },
  actions: {
    setErrorSource: ({ context }, params: { source: string }) => {
      context.errorSource = params.source
    },
  },
  guards: {
    checkGoToParam: () => isGotoParam("screening-authorize"), // Has goto if user is coming from auth
    checkNoPmUuid: () => {
      const pm_uuid = useCookies().get(COOKIES.PM_UUID)
      return !pm_uuid
    },
    checkScreeningToken: () => {
      // Has token if the user closed the screening tab
      const token = localStorage.getItem("screening-token")
      return !!token
    },
  },
}).createMachine({
  id: "screeningMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "screeningLoading") {
            router.replace({ name: "screeningLoading" })
          }
        },
      ],
      invoke: {
        src: "loadScreeningData",
        onDone: [
          {
            guard: ({ event }) => event.output?.type === "consent_needed",
            target: "intro",
          },
          {
            guard: ({ event }) => event.output?.type === "redirected",
            target: "final",
          },
          {
            target: "checkConditions",
          },
        ],
        onError: {
          actions: () => {
            globalActor.send({ type: "GLOBAL.SCREENING.DONE" })
          },
        },
      },
    },
    checkConditions: {
      always: [
        { target: "noPmUuid", guard: "checkNoPmUuid" },
        // We want to skip intro page if the user has already seen it so redirect to authorize
        { target: "authorize", guard: "checkGoToParam" },
        { target: "authorize", guard: "checkScreeningToken" },
        { target: "intro" },
      ],
    },
    noPmUuid: {
      entry: [
        () => {
          globalActor.send({ type: "GLOBAL.SCREENING.DONE" }) // We cannot access screening if we don't have a pm_uuid
        },
      ],
    },
    intro: {
      entry: [
        removeGotoParam,
        () => router.replace({ name: "screeningIntro" }),
      ],
      on: {
        introDone: { target: "authorize" },
      },
    },
    authorize: {
      entry: [
        () => deleteSessionItem(), // We can delete screening origin because the user can access the workflow
        () => router.replace({ name: "screeningAuthorize" }),
      ],
      on: {
        done: { target: "roomLoading" },
      },
    },
    roomLoading: {
      entry: [
        () => deleteSessionItem(),
        () => router.replace({ name: "screeningRoomLoading" }),
      ],
      on: {
        done: { target: "roomTest" },
        error: { target: "screeningError" },
      },
    },
    screeningError: {
      entry: [
        ({ context }) =>
          router.replace({
            name: "screeningError",
            query: context.errorSource
              ? { source: context.errorSource }
              : undefined,
          }),
      ],
    },
    roomTest: {
      entry: [
        () => deleteSessionItem(),
        () => router.replace({ name: "screeningTest" }),
      ],
      on: {
        error: { target: "screeningError" },
        done: { target: "screeningReview" },
        loadResults: { target: "screeningLoadResults" },
      },
    },
    screeningReview: {
      entry: [() => router.replace({ name: "screeningReview" })],
      on: {
        done: { target: "screeningLoadResults" },
        error: { target: "screeningError" },
      },
    },
    screeningLoadResults: {
      entry: [() => router.replace({ name: "screeningLoadResults" })],
      on: {
        error: {
          target: "screeningError",
          actions: {
            type: "setErrorSource",
            params: { source: "results" }, // Show screening error page with ?source=results for generic texts
          },
        },
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let screeningActor = createActor(screeningMachine)

const screeningActorObserver = {
  async complete() {
    deleteSessionItem()

    const userStore = useUserStore()
    const cookies = useCookies()
    const { fetchScreeningData } = useAnswersStore()
    const { isGuestAcc, isResellerAcc, nextAssessmentName, user } =
      storeToRefs(userStore)
    const { logoutAndRedirectToAuth } = usePendingUser()
    const { clearScreeningStorage, clearScreeningCookies } = useAnswersStore()

    await fetchScreeningData()

    const userEmail = user.value.user.email

    // If reseller account, redirect to thanks ATS page
    if (isResellerAcc.value) {
      globalActor.send({ type: "GLOBAL.SCREENING.RESELLER.DONE" })
      return
    }

    // If no next assessment, redirect to screening done
    if (!nextAssessmentName.value) {
      const cookiesToClear = [COOKIES.PM_UUID, COOKIES.COMPANY_UUID]
      cookiesToClear.forEach((cookie) =>
        cookies.remove(cookie, COOKIES.OPTIONS)
      )

      globalActor.send({ type: "GLOBAL.SCREENING.DONE" })
      return
    }

    // If next assessment is meeting, redirect to meeting
    if (nextAssessmentName.value === "meeting") {
      // Pending guest user can book meeting
      globalActor.send({
        type: `GLOBAL.REDIRECT.MEETING` as GlobalMachineEvent,
      })
      return
    }

    // If guest account, redirect to register
    if (isGuestAcc.value) {
      // Guest user cannot pass other assessment make him register
      clearScreeningStorage()
      clearScreeningCookies()
      logoutAndRedirectToAuth(userEmail)
    } else {
      // Redirect to next assessment
      await userStore.fetchAssessmentSummary()
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    }
  },
}

screeningActor.subscribe(screeningActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const screeningActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToScreeningActor(
  observer: (_snapshot: unknown) => void
) {
  screeningActorObservers.push(observer)
  screeningActor.subscribe(observer)
}

let hasScreeningActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startScreeningActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "screening"

  if (hasScreeningActorStarted && isEnteringAssessment) {
    screeningActor.stop()
    screeningActor = createActor(screeningMachine)
    screeningActor.subscribe(screeningActorObserver)
    for (const observer of screeningActorObservers)
      screeningActor.subscribe(observer)
  }

  hasScreeningActorStarted = true
  screeningActor.start()
}
